/**
 * Iter 34 — Admin View MVP shared shell.
 *
 * FP3-029B5 — The top "Admin View · Coordination & Oversight" banner
 * and the horizontal subnav (Dashboard / Shared Calendar / Providers /
 * Students / Coverage & Risk) have been removed because they
 * duplicated the left sidebar after the FP3-029B3 cleanup. The shell
 * is now a thin page-header wrapper: title + optional subtitle +
 * optional right-side action cluster. The left sidebar's Admin View
 * section is the single source of truth for admin navigation.
 *
 * The `compact` prop is preserved for backward compatibility and is
 * a no-op now that the global chrome is gone — any caller that still
 * passes it stays valid.
 *
 * FP3-053 — Adds an optional compact "Viewing: …" pill rendered to
 * the right of the page title when a super admin is operating with
 * the sidebar context selector pinned. Replaces the deprecated
 * full-width blue `PlatformViewBanner` so operational pages get
 * back their vertical real estate while the context stays obvious.
 */
import { useSuperAdminContext } from "@/lib/useSuperAdminContext";
import { Buildings, GraduationCap } from "@phosphor-icons/react";
import AdminNotificationsBell from "@/components/admin/AdminNotificationsBell";

function ContextPill() {
  const { isSuperAdmin, orgId, orgName, schoolName } = useSuperAdminContext();
  if (!isSuperAdmin || !orgId) return null;
  const label =
    orgName && schoolName
      ? `${orgName} · ${schoolName}`
      : orgName || "Loading…";
  return (
    <span
      data-testid="admin-shell-context-pill"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E6F1F8] border border-[#1F4F73]/30 text-[11.5px] text-[#0F3554]"
      title="Active org/school context (set via sidebar)"
    >
      {schoolName ? (
        <GraduationCap size={12} weight="duotone" className="text-[#1F4F73]" />
      ) : (
        <Buildings size={12} weight="duotone" className="text-[#1F4F73]" />
      )}
      <span className="font-medium truncate max-w-[36ch]">Viewing: {label}</span>
    </span>
  );
}

export default function AdminShell({ children, title, subtitle, actions }) {
  return (
    <div className="p-6 max-w-7xl min-[1920px]:max-w-[1800px] mx-auto" data-testid="admin-shell">
      {/* Page header */}
      <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1
              className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight"
              data-testid="admin-page-title"
            >
              {title}
            </h1>
            <ContextPill />
          </div>
          {subtitle && (
            <p className="text-[12px] text-[#6B7280] mt-1">{subtitle}</p>
          )}
        </div>
        {/* FP3-093A — bell renders on every admin page. */}
        <div className="flex items-center gap-2">
          <AdminNotificationsBell />
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>

      {children}
    </div>
  );
}
